import { useQuery } from "@tanstack/react-query";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Target, Trophy } from "lucide-react";

/**
 * Coach Mode v2 (Task #311) — quantified aspiration progress bars, rendered
 * at the top of the Calendar Hub coach panel. Each measurable aspiration gets
 * a visible bar (current / target unit) that fills as real bookings/payments
 * land or as the user reports progress in chat/voice.
 *
 * Confetti: completion confetti is owned app-wide by the
 * GlobalGamificationCelebrations bus, which detects a bar crossing 100% from
 * the gamification snapshot and fires a deduped, cross-tab burst (it also
 * stacks on top of any booking/win celebration in the same tick). This panel
 * is purely a visualization — it never fires its own confetti, which avoids
 * retroactive/double bursts on first load.
 */
type AspirationRow = {
  id: string;
  title: string;
  domain: string;
  why: string | null;
  target: number | null;
  current: number;
  unit: string | null;
  pct: number | null;
  status: string;
  completedAt: string | null;
};

export function CoachAspirationsPanel() {
  const { data, isLoading } = useQuery<AspirationRow[]>({
    queryKey: ["/api/coach/aspirations", { status: "active" }],
    refetchInterval: 30_000,
  });

  const quantified = (data ?? []).filter((a) => a.target != null && a.target > 0);

  if (isLoading) {
    return (
      <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground italic" data-testid="text-coach-aspirations-loading">
        Loading your aspirations…
      </div>
    );
  }

  if (quantified.length === 0) return null;

  return (
    <div className="px-3 pt-3 pb-1 space-y-2" data-testid="container-coach-aspirations">
      <div className="flex items-center gap-2">
        <Target className="h-3.5 w-3.5 text-muted-foreground" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Aspirations
        </h4>
      </div>
      <div className="space-y-2.5">
        {quantified.map((a) => {
          const pct = Math.max(0, Math.min(1, a.pct ?? 0));
          const pctWhole = Math.round(pct * 100);
          const done = pct >= 1;
          const unit = a.unit ? ` ${a.unit}` : "";
          return (
            <div
              key={a.id}
              className="rounded-md border p-2.5 space-y-1.5"
              data-testid={`row-aspiration-${a.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className="text-sm font-medium leading-snug line-clamp-2"
                  data-testid={`text-aspiration-title-${a.id}`}
                >
                  {a.title}
                </span>
                {done ? (
                  <Badge
                    variant="default"
                    className="shrink-0 gap-1 bg-green-600 hover:bg-green-600"
                    data-testid={`badge-aspiration-done-${a.id}`}
                  >
                    <Trophy className="h-3 w-3" /> Done
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="shrink-0 text-xs capitalize"
                    data-testid={`badge-aspiration-domain-${a.id}`}
                  >
                    {a.domain}
                  </Badge>
                )}
              </div>
              <Progress
                value={pctWhole}
                className="h-2"
                data-testid={`progress-aspiration-${a.id}`}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid={`text-aspiration-progress-${a.id}`}>
                  {a.current.toLocaleString()} / {a.target!.toLocaleString()}{unit}
                </span>
                <span data-testid={`text-aspiration-pct-${a.id}`}>{pctWhole}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
